import { type ApiTransportadorGetParams, apiTransportadorGet } from "thirdParty/transportadores/api/apiTransportadorGet";
import type { ApiTransportador } from "thirdParty/transportadores/types/ApiTransportador";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { Empty, Select, Spin } from "antd";
import debounce from "lodash/debounce";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { formatDocument } from "std/format";

const QTDE_MIN_CHARS = 3;
const debounceTimeout = 600;

type SelectTransportadorMultipleProps = {
	value?: number[];
	selectedTransportadores?: ApiTransportador[];
	onChange?: (value: number[], transportadores: ApiTransportador[]) => void;
	placeholder?: string;
	disabled?: boolean;
	externalParams?: Partial<ApiTransportadorGetParams>;
};

function getTransportadorLabel(transportador: ApiTransportador): string {
	return transportador?.pessoa?.pes_nome_razaosocial || String(transportador.tra_idpk);
}

function isTransportadorHydrated(transportador: ApiTransportador | undefined): boolean {
	return Boolean(transportador?.pessoa?.pes_nome_razaosocial);
}

async function fetchTransportadorById(id: number): Promise<ApiTransportador | undefined> {
	const response = await apiTransportadorGet({ idpk: id });
	return response[0];
}

export function SelectTransportadorMultiple({
	value = [],
	selectedTransportadores = [],
	onChange,
	placeholder,
	disabled,
	externalParams,
}: SelectTransportadorMultipleProps) {
	const [searchValue, setSearchValue] = useState("");

	const params: ApiTransportadorGetParams = useMemo(
		() => ({
			...externalParams,
			pesquisar: searchValue.length >= QTDE_MIN_CHARS ? searchValue : undefined,
			qtde_registros: 1000,
		}),
		[searchValue, externalParams],
	);

	const { data, isFetching } = useQuery({
		queryKey: ["apiTransportadorGet", "multiple", params],
		queryFn: () => apiTransportadorGet({ params }),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
	});

	const idsNeedingFetch = useMemo(() => {
		return value.filter((id) => {
			const selected = selectedTransportadores.find((transportador) => transportador.tra_idpk === id);
			return !isTransportadorHydrated(selected);
		});
	}, [value, selectedTransportadores]);

	const fetchedTransportadoresQueries = useQueries({
		queries: idsNeedingFetch.map((id) => ({
			queryKey: ["apiTransportadorGet", "multiple", "byId", id],
			queryFn: () => fetchTransportadorById(id),
			staleTime: 5 * 60 * 1000,
		})),
	});

	const fetchedTransportadores = useMemo(() => {
		return fetchedTransportadoresQueries
			.map((query) => query.data)
			.filter((transportador): transportador is ApiTransportador => Boolean(transportador));
	}, [fetchedTransportadoresQueries]);

	useEffect(() => {
		if (!onChange || fetchedTransportadores.length === 0) {
			return;
		}

		const enrichedMap = new Map(fetchedTransportadores.map((transportador) => [transportador.tra_idpk, transportador]));
		let changed = false;

		const nextSelected = value.map((id) => {
			const enriched = enrichedMap.get(id);
			const current = selectedTransportadores.find((transportador) => transportador.tra_idpk === id);

			if (enriched && !isTransportadorHydrated(current)) {
				changed = true;
				return enriched;
			}

			return current || enriched || ({ tra_idpk: id } as ApiTransportador);
		});

		if (changed) {
			onChange(value, nextSelected);
		}
	}, [fetchedTransportadores, onChange, selectedTransportadores, value]);

	const handleSearch = useMemo(() => {
		return debounce((nextValue: string) => {
			setSearchValue(nextValue);
		}, debounceTimeout);
	}, []);

	const handleChange = (nextValue: number[]): void => {
		const searchResults = data || [];
		const nextSelected = nextValue.map((id) => {
			return (
				searchResults.find((item) => item.tra_idpk === id) ||
				fetchedTransportadores.find((item) => item.tra_idpk === id) ||
				selectedTransportadores.find((item) => item.tra_idpk === id) ||
				({ tra_idpk: id } as ApiTransportador)
			);
		});

		onChange?.(nextValue, nextSelected);
	};

	const options = useMemo(() => {
		const mapped = new Map<number, ApiTransportador>();

		for (const transportador of selectedTransportadores) {
			mapped.set(transportador.tra_idpk, transportador);
		}

		for (const transportador of fetchedTransportadores) {
			mapped.set(transportador.tra_idpk, transportador);
		}

		for (const transportador of data || []) {
			mapped.set(transportador.tra_idpk, transportador);
		}

		return Array.from(mapped.values()).map((item) => ({
			value: item.tra_idpk,
			label: getTransportadorLabel(item),
			transportador: item,
		}));
	}, [data, fetchedTransportadores, selectedTransportadores]);

	const renderNotFoundData = (): ReactNode => {
		if (searchValue.length > 0 && searchValue.length < QTDE_MIN_CHARS) {
			return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Digite ao menos 3 caracteres" />;
		}

		return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
	};

	return (
		<Select
			mode="multiple"
			allowClear={true}
			showSearch={true}
			maxTagCount="responsive"
			placeholder={placeholder || "Selecione um ou mais transportadores"}
			filterOption={false}
			disabled={disabled}
			loading={isFetching || fetchedTransportadoresQueries.some((query) => query.isFetching)}
			value={value}
			options={options}
			optionLabelProp="label"
			className="w-full"
			notFoundContent={isFetching ? <Spin size="small" /> : renderNotFoundData()}
			onSearch={handleSearch}
			onChange={handleChange}
			onClear={() => {
				setSearchValue("");
				onChange?.([], []);
			}}
			optionRender={(option) => {
				const transportador = option.data.transportador as ApiTransportador | undefined;

				if (!transportador?.pessoa) {
					return option.label;
				}

				return (
					<>
						<div className="font-bold">{transportador.pessoa.pes_nome_razaosocial || "-"}</div>
						{transportador.tra_codigoindividual != null ? (
							<div style={{ fontSize: "13px", opacity: "0.7" }}>Código: {transportador.tra_codigoindividual}</div>
						) : null}
						{transportador.pessoa.pes_cnpj_cpf ? (
							<div style={{ fontSize: "13px", opacity: "0.7" }}>{formatDocument(transportador.pessoa.pes_cnpj_cpf)}</div>
						) : null}
					</>
				);
			}}
		/>
	);
}
